import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useVideo } from "../../../hooks/useVideo";
import ConfirmationModal from "../../confirmation/ConfirmationModal";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import LoadingSpinner from "../../loader/Loader";
import {
  useDeletePlanMutation,
  useGetPlansQuery,
} from "../../../features/api/plansApiSlice";

const AdminPlansList = () => {
  const navigate = useNavigate();
  const { formatDate } = useVideo();
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deletePlan] = useDeletePlanMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page

  const {
    data: plans = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetPlansQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!plans?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [plans?.length, isFetching, isError, refetch]);

  const [planFilter, setPlanFilter] = useState("");

  // Filtered transactions
  const filteredPlans = useMemo(() => {
    if (!plans) return [];
    return plans.filter((tx) => {
      const matchesPlan =
        !planFilter ||
        `${tx?.title} ${tx?.description}`
          .toLowerCase()
          .includes(planFilter.toLowerCase());
      return matchesPlan;
    });
  }, [plans, planFilter]);

  const totalPages = Math.ceil(filteredPlans.length / pageSize);

  const currentVideos = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredPlans.slice(startIdx, startIdx + pageSize);
  }, [filteredPlans, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (planId) => {
    setPlanToDelete(planId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    try {
      const response = await deletePlan({ planId: planToDelete }).unwrap();
      toast.success(response?.message || "Plan deleted successfully!");
      setPlanToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to delete plan:", err);
      toast.error(err?.data?.message || "Failed to delete plan.");
    }
  };

  const handleCancelDelete = () => {
    setPlanToDelete(null);
    setIsModalOpen(false);
  };

  if (isLoading) return <LoadingSpinner />;

  if (!plans || plans?.length === 0) {
    return (
      <div className="no-content">
        <div>
          <p>No plans found</p>
        </div>
      </div>
    );
  } else if (isError)
    return (
      <div className="no-content">
        <div>
          <p>Error loading plans</p>
        </div>
      </div>
    );

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Total Plans {plans?.length}</h1>
          <button onClick={() => navigate("/admin/plans/add-plan")}>
            Add Plan
          </button>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by title or last des"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
        />
      </div>

      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>title</th>
            <th>Plan Type</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentVideos?.map((tx, index) => (
            <tr key={tx?._id}>
              <td>{index + 1}</td>
              <td>{tx?.title}</td>
              <td>{tx?.planType}</td>
              <td>{formatDate(tx?.createdAt)}</td>
              <td className="actions-container">
                <button
                  className="refund-btn"
                  onClick={() => handleDeleteClick(tx?.id || tx?._id)}
                >
                  Delete
                </button>
                <Link
                  className="ed-button"
                  aria-label={`edit ${tx?.title}`}
                  to={`/admin/plans/edit-plan/${tx?._id}`}
                >
                  Edit
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
        <ConfirmationModal
          isOpen={isModalOpen}
          message="Are you sure you want to delete this Video? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPlansList;
